import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, ButtonGroup, NavigationHeader, Spacer } from 'design-system';
import AmountSelectors from 'design-system/AmountSelectors';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { savePotsOnboardingState } from 'features/pots/actions';
import { AUTOSAVE_DEFAULT_MONTHLY_AMOUNT, AUTOSAVE_DEFAULT_WEEKLY_AMOUNT, AUTOSAVE_MIN_AMOUNT, AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS, AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS, } from 'features/pots/constants';
import { getPeriodFromRRule } from 'features/pots/utils';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
const AutosaveChooseCustomContributions = ({ route, navigation, }) => {
    const { collectionFrequency } = route.params;
    const moneyInputRef = useRef(null);
    const colors = useColors();
    const period = getPeriodFromRRule(collectionFrequency.rrule);
    const isMonthly = period === 'Monthly';
    const defaultAmount = isMonthly ? AUTOSAVE_DEFAULT_MONTHLY_AMOUNT : AUTOSAVE_DEFAULT_WEEKLY_AMOUNT;
    const dispatch = useAppDispatch();
    const hasAutomation = useAppSelector((state) => state.pots.onboarding?.automated);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        Amplitude.logEvent('Choose Direct Debit Amount', { source: 'save' });
    }, []);
    const styles = useStyles(styleSet);
    const [amount, setAmount] = useState(defaultAmount);
    const paddingBottomStyles = useMarginBottom('paddingBottom');
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const outputColors = useMemo(() => [colors.background.dark, colors.background.dark], [colors.background.dark]);
    const scrollY = useSharedValue(0);
    const handleContinue = useCallback(() => {
        dispatch(savePotsOnboardingState({ contributionAmount: amount }));
        navigation.navigate('AutosaveChooseRoundupContributions', { collectionFrequency });
    }, [amount, collectionFrequency]);
    const handleNotNow = useCallback(() => {
        dispatch(savePotsOnboardingState({ contributionAmount: null }));
        navigation.navigate('AutosaveChooseRoundupContributions', { collectionFrequency });
    }, [collectionFrequency]);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <AmountSelectors amounts={isMonthly ? AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS : AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS} moneyInputRef={moneyInputRef}/>
      </View>), [isMonthly, styles.numPadHeader]);
    return (<AppView type="secondary">
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} rightContent={<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>}/>
      <View style={[styles.container, paddingBottomStyles]}>
        <ScreenInfoHeader title={`Choose ${isMonthly ? 'monthly' : 'weekly'} contribution`} style={styles.screenInfoHeader}/>
        <View style={styles.flex}>
          <MoneyInput flex showMinHint showMaxHint ref={moneyInputRef} min={AUTOSAVE_MIN_AMOUNT} colors={moneyInputColors} onValueChange={setAmount} alwaysDisplayMaxHint={false} alwaysDisplayMinHint={false} max={isMonthly ? 1500 : 400} initialValue={defaultAmount} numPadHeader={numPadHeader}/>
          <Spacer h={20}/>
        </View>
        <ButtonGroup>
          {!!hasAutomation && <Button title="Not now" brandReversed onPress={handleNotNow}/>}
          <Button brand title="Continue" disabled={amount < AUTOSAVE_MIN_AMOUNT} onPress={handleContinue}/>
        </ButtonGroup>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    flex: {
        flex: 1,
    },
    screenInfoHeader: {
        paddingBottom: 0,
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
}));
export default AutosaveChooseCustomContributions;
